import {
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  LoadingSpinner,
  ScrollArea,
  Separator,
  useToast,
} from '@ais-ui/ui-core';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  ParsedReportParams,
  ReportTemplate,
  useCloneReport,
  useGetFilters,
  useGetShareGroups,
} from '@ais-ui/websockets';
import {
  FilterType,
  MiscellaneousField,
  ReportShapeItem,
  reportTypesObject,
  ShapeType,
} from '../../reportsTypes';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { FilterFormField } from './FilterFormField';
import { ShapeFormField } from './ShapeFormField';
import { getReportParams } from '../CreateReportTemplate/getReportParams';
import {
  createPortDictionary,
  createShapeDictionary,
  createWorldDictionary,
} from '../utils/createShapeDictionary';
import {
  useDatabaseRange,
  useFlattenedGeometryShapes,
  usePortsGetList,
} from '@ais-ui/api';
import { extractNamesFromShape } from '../utils/extractNames';
import { extractMatchingData } from '../utils/extractMatchingData';
import { createFilter } from '../utils/createFilter';
import { createDynamicShapeZodSchema } from '../utils/createDynamicShapeZodSchema';
import { createDynamicFilterZodSchema } from '../utils/createDynamicFilterZodSchema';
import { createDynamicMiscellaneousZodSchema } from '../utils/createDynamicMiscellaneousZodSchema';
import { findFilterTypeFromReport } from '../utils/findFilterTypeFromReport';
import { findShapeTypeFromReport } from '../utils/findShapeTypeFromKey';
import { MiscellaneousFormField } from './MiscellaneousFormField';
import { useInitializeShape } from './initializeReportValues/useInitializeShape';
import { useInitializeFilter } from './initializeReportValues/useInitializeFilter';
import { useInitializeMiscellaneous } from './initializeReportValues/useInitializeMiscellaneous';
import { useLocalization } from '@ais-ui/locale-management';
import { PiEnvelopeSimpleThin, PiFlag, PiPhone } from 'react-icons/pi';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { DateTimePickerField } from '../../../utils/DatetimePickerField';
import { createReportTemplatePeriodFormSchema } from '../utils/createPeriodFormSchema';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { ShapeDictionaryType } from '../../utils';
import { isEmpty } from 'lodash';

interface CloneReportProps {
  children: ReactElement;
  setShowPopover: (showPopover: boolean) => void;
  report: ParsedReportParams | undefined;
  reportTemplate: ReportTemplate;
}

export type ReportCloneValues = z.infer<
  ReturnType<typeof createReportCloneFormSchema>
>;

const reportCloneFormBaseSchema = z.object({
  report_name: z.string().optional(),
  start_datetime: z.date(),
  end_datetime: z.date(),
});

function createReportCloneFormSchema(
  shapeItems: ReportShapeItem[] | undefined,
  shapeType: ShapeType,
  filterType: FilterType,
  miscellaneousFields: Record<string, MiscellaneousField> | undefined
) {
  const baseSchema = reportCloneFormBaseSchema;

  const filterSchema = createDynamicFilterZodSchema(filterType);
  const shapeSchema = createDynamicShapeZodSchema(shapeItems, shapeType);
  const miscellaneousSchema =
    createDynamicMiscellaneousZodSchema(miscellaneousFields);

  const nestedMiscellaneousSchema = z.object({
    miscellaneous: miscellaneousSchema,
  });

  const schema = baseSchema
    .merge(filterSchema)
    .merge(shapeSchema)
    .merge(nestedMiscellaneousSchema);

  return schema;
}

const useDynamicFormSetup = (
  shapeItems: ReportShapeItem[] | undefined,
  shapeType: ShapeType,
  filterType: FilterType,
  miscellaneousFields: Record<string, MiscellaneousField> | undefined,
  initialValues: Record<string, any>
) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { data: databaseRange, isLoading: isDatabaseRangeLoading } =
    useDatabaseRange();

  // Create the form schema only when databaseRange is available
  const reportCloneFormSchema = useMemo(() => {
    return createReportCloneFormSchema(
      shapeItems,
      shapeType,
      filterType,
      miscellaneousFields
    );
  }, [shapeItems, shapeType, filterType, miscellaneousFields]);

  // Apply refinements to schema only when databaseRange is available
  const schema = useMemo(() => {
    if (!databaseRange) return reportCloneFormSchema;

    const schemaWithRefinement = reportCloneFormSchema.refine(
      (data) => data.end_datetime > data.start_datetime,
      {
        message: messages.reportsCloneTimeValidationText,
        path: ['end_datetime'],
      }
    );

    const startDatetimeThreshold = databaseRange?.database.begin;
    const endDatetimeThreshold = databaseRange?.database.end;

    return createReportTemplatePeriodFormSchema(
      startDatetimeThreshold,
      endDatetimeThreshold,
      formatDateTimeWithTimeZone(startDatetimeThreshold),
      formatDateTimeWithTimeZone(endDatetimeThreshold),
      schemaWithRefinement
    );
  }, [
    databaseRange,
    reportCloneFormSchema,
    messages,
    formatDateTimeWithTimeZone,
  ]);

  // We use the useEffect with reset to set initial values. Do not use defaultValues as it will ruin the
  //  DateRangePicker
  const form: UseFormReturn<ReportCloneValues> = useForm<ReportCloneValues>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!isDatabaseRangeLoading && databaseRange) {
      form.reset(initialValues);
    }
  }, [form, initialValues, isDatabaseRangeLoading, databaseRange]);

  return {
    form,
    isDatabaseRangeLoading,
  };
};

export const CloneReport = ({
  children,
  setShowPopover,
  report,
  reportTemplate,
}: CloneReportProps) => {
  const { messages } = useLocalization();
  const { mutate } = useCloneReport();
  const { toast } = useToast();
  const { data: shapes, isLoading: isLoadingShapes } =
    useFlattenedGeometryShapes();
  const { data: filters, isLoading: isLoadingFilters } = useGetFilters();
  const { data, isLoading: isLoadingPorts } = usePortsGetList();
  const { isLoading: isLoadingGroups } = useGetShareGroups();
  const { data: databaseRange } = useDatabaseRange();
  const ports = useMemo(() => data?.records || [], [data]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dynamicShape, setDynamicShape] = useState<GeoJson>();
  const [isDynamic, setIsDynamic] = useState(false);

  const reportType = useMemo(() => {
    return reportTypesObject[reportTemplate.type];
  }, [reportTemplate.type]);
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const [filterType, setFilterType] = useState<FilterType>(
    findFilterTypeFromReport(report, reportType)
  );
  const [shapeType, setShapeType] = useState<ShapeType>(
    findShapeTypeFromReport(report)
  );

  useEffect(() => {
    const shapeRaw = report?.params['GAC']?.shape;
    if (!shapeRaw) return;

    let shape;
    try {
      shape = typeof shapeRaw === 'string' ? JSON.parse(shapeRaw) : shapeRaw;
    } catch (e) {
      console.warn('Invalid shape JSON:', e);
      return;
    }

    const name = shape?.properties?.name;
    if (name === 'Shape drawn in MDM') {
      setIsDynamic(true);
      setDynamicShape(shape);
    }
  }, [report?.params]);
  const initializeShape = useInitializeShape(report, reportType);
  const initializeFilter = useInitializeFilter(report, reportType);
  const initializeMiscellaneous = useInitializeMiscellaneous(
    report,
    reportType
  );
  const shape = 'Shape drawn in MDM';
  const initialValues = useMemo(() => {
    return {
      report_name: report?.info?.['Job name'] ? report?.info?.['Job name'] : '',
      start_datetime: new Date(report?.params['SDAISO']),
      end_datetime: new Date(report?.params['EDAISO']),
      miscellaneous: initializeMiscellaneous(),
      ...initializeFilter(),
      ...(isDynamic ? { shape } : initializeShape()),
    };
  }, [
    report,
    isDynamic,
    initializeMiscellaneous,
    initializeFilter,
    initializeShape,
  ]);

  // This code removes the trailing 's' character from the initial value of the
  // interval field in the form. Without this adjustment, the form would interpret
  // the interval value incorrectly (e.g., "years", "months") and cause an error,
  // as it expects a value without the trailing 's'.
  // TODO: Update the miscellaneous getter function to handle form values properly
  // and ensure the interval field is returned in the expected format.
  const maybeInterval = (initialValues.miscellaneous as any)?.interval;
  if (typeof maybeInterval === 'string' && maybeInterval.endsWith('s')) {
    (initialValues.miscellaneous as { interval: string }).interval =
      maybeInterval.slice(0, -1);
  }

  const { form, isDatabaseRangeLoading } = useDynamicFormSetup(
    reportType.shape ? reportType.shape[shapeType] : undefined,
    shapeType,
    filterType,
    reportType.miscellaneous,
    initialValues
  );
  const isLoading = useMemo(() => {
    return (
      isLoadingShapes || isLoadingFilters || isLoadingPorts || isLoadingGroups
    );
  }, [isLoadingShapes, isLoadingFilters, isLoadingPorts, isLoadingGroups]);
  useEffect(() => {
    if (!isLoading) {
      form.reset(initialValues);
    }
  }, [isLoading, form, initialValues]);

  const phoneActive = false;

  const onClone = (data: ReportCloneValues) => {
    setIsSubmitting(true);
    if (!shapes) {
      setIsSubmitting(false);
      return;
    }

    if (!filters) {
      setIsSubmitting(false);
      return;
    }

    let shapeDictionary = null;
    if (reportType.shape) {
      const shapeNames = extractNamesFromShape(reportType.shape, shapeType);
      const shapeData = extractMatchingData(data, shapeNames);
      if (shapeType === 'shape') {
        shapeDictionary = createShapeDictionary(shapeData, shapes);
      } else if (shapeType === 'multiShape') {
        shapeDictionary = createShapeDictionary(shapeData, shapes);
      } else if (shapeType === 'ports') {
        shapeDictionary = createPortDictionary(shapeData, ports);
      } else if (shapeType === 'worldShape') {
        shapeDictionary = createWorldDictionary(shapeData);
      }
    }

    if (isEmpty(shapeDictionary)) {
      shapeDictionary = {
        shape: dynamicShape,
      } as ShapeDictionaryType;
    }

    let filter = null;
    if (reportType.filter) {
      filter = createFilter(data, filters, filterType);
    }

    const reportTemplateName = report?.params.RDN;
    const reportTemplateId = report?.params.RDU;

    // selectedGroups, email, countryCode and phone are present for all reports
    const miscellaneous: Record<string, any> = {
      selectedGroups:
        (data?.miscellaneous as undefined | Record<string, any>)
          ?.selectedGroups ?? [],
      email: data.email,
      countryCode: data.countryCode,
      phone: data.phone,
    };

    const dynamicMisc = data?.miscellaneous as Record<string, any>;

    if (reportType.miscellaneous) {
      Object.keys(reportType.miscellaneous).forEach((key) => {
        miscellaneous[key] = dynamicMisc?.[key];
      });
    }

    const reportName = data.report_name ? data.report_name : 'N/A';

    const params = getReportParams(
      reportType,
      reportTemplateName,
      reportName,
      shapeDictionary,
      data.start_datetime,
      data.end_datetime,
      miscellaneous,
      filter,
      reportTemplateId,
      shapeType
    );

    if (!params) {
      setIsSubmitting(false);
      return;
    }

    // Not sure what this is, but if it's not present the report fails
    params['RSE'] = false;

    const callbacks = {
      onSuccess: () => {
        toast({
          className: 'clone-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsCloneSuccessMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
        setShowPopover(false);
      },
      onError: (error: Error) => {
        let text = messages.reportsCloneFailedGenericMessage;
        if (error.message.includes('jobs exceeded')) {
          text = messages.reportsCloneFailedJobsExceededMessage;
        }
        toast({
          className: 'clone-report-error-toast',
          title: messages.genericToastTitleError,
          description: text,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        setShowDialog(false);
        setShowPopover(false);
      },
    };
    mutate({ type: reportType.type, params }, callbacks);
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="p-8 md:p-12 w-full max-w-lg md:max-w-xl lg:max-w-3xl h-auto min-h-[360px] md:min-h-[640px] lg:min-h-[840px]">
        {isLoading && <LoadingSpinner></LoadingSpinner>}
        {!isLoading && (
          <div>
            <div className="h-fit pb-10">
              <h3 className="text-lg font-medium">
                {messages.reportsCloneReportTitle}
              </h3>
              <p className="text-sm text-muted-foreground">
                {messages.reportsCloneReportSubtitle}
              </p>
            </div>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onClone)}
                className="flex flex-col h-[654px]"
              >
                <div className="flex-grow">
                  <ScrollArea className="flex-grow overflow-y-auto h-[400px] md:h-[500px] lg:h-[574px] pr-4">
                    <div className="space-y-8">
                      <FormField
                        control={form.control}
                        name="report_name"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between">
                              <FormLabel>
                                {messages.reportsCloneReportNameLabel}
                              </FormLabel>
                              <InfoIconTooltip>
                                {messages.reportsCloneReportNameTooltip}
                              </InfoIconTooltip>
                            </div>
                            <FormControl onChange={() => form.clearErrors()}>
                              <Input
                                type="text"
                                placeholder={
                                  messages.reportsCloneReportNamePlaceholder
                                }
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="start_datetime"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>
                              {messages.reportsCreateReportStartDateLabel ||
                                'Start Date'}{' '}
                              *
                            </FormLabel>
                            <FormControl>
                              <DateTimePickerField
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                                dateLimit="past"
                                startDatetimeThreshold={
                                  databaseRange?.database.begin
                                }
                                endDatetimeThreshold={
                                  databaseRange?.database.end
                                }
                                className="w-80"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="end_datetime"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>
                              {messages.reportsCreateReportEndDateLabel ||
                                'End Date'}{' '}
                              *
                            </FormLabel>
                            <FormControl>
                              <DateTimePickerField
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                                dateLimit="past"
                                startDatetimeThreshold={
                                  databaseRange?.database.begin
                                }
                                endDatetimeThreshold={
                                  databaseRange?.database.end
                                }
                                className="w-80"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {reportType.filter && (
                        <FilterFormField
                          form={form}
                          filterFields={reportType.filter}
                          filterType={filterType as FilterType}
                          setFilterType={setFilterType}
                        ></FilterFormField>
                      )}
                      {reportType.shape && (
                        <ShapeFormField
                          form={form}
                          shapeFields={reportType.shape}
                          shapeType={shapeType as ShapeType}
                          setShapeType={setShapeType}
                        ></ShapeFormField>
                      )}
                      {reportType.miscellaneous && (
                        <MiscellaneousFormField
                          form={form}
                          miscellaneousFields={reportType.miscellaneous}
                        ></MiscellaneousFormField>
                      )}
                    </div>
                    <Separator className="my-6"></Separator>
                    <div className="space-y-6">
                      <Label className="text-md">
                        {messages.reportsCreateReportMiscellaneousNotifyLabel}
                      </Label>
                      <FormField
                        key="email"
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between">
                              <FormLabel>
                                {
                                  messages.reportsCreateReportMiscellaneousNotifyEmailLabel
                                }
                              </FormLabel>
                              <InfoIconTooltip>
                                {
                                  messages.reportsCreateReportMiscellaneousNotifyEmailTooltip
                                }
                              </InfoIconTooltip>
                            </div>
                            <FormControl onChange={() => form.clearErrors()}>
                              <div className="relative">
                                <PiEnvelopeSimpleThin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  className="pl-9"
                                  type="text"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        key="selectedGroups"
                        control={form.control}
                        name="miscellaneous.selectedGroups"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between">
                              <FormLabel>
                                {
                                  messages.reportsCreateReportMiscellaneousShareWithLabel
                                }
                              </FormLabel>
                              <InfoIconTooltip>
                                {
                                  messages.reportsCreateReportMiscellaneousShareWithTooltip
                                }
                              </InfoIconTooltip>
                            </div>
                            <FormControl onChange={() => form.clearErrors()}>
                              <GroupMultiSelect {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {phoneActive && (
                        <div>
                          <div className="flex justify-between mb-2">
                            <FormLabel>
                              {
                                messages.reportsCreateReportMiscellaneousNotifyPhoneLabel
                              }
                            </FormLabel>
                            <InfoIconTooltip>
                              {
                                messages.reportsCreateReportMiscellaneousNotifyPhoneTooltip
                              }
                            </InfoIconTooltip>
                          </div>
                          <div className="flex w-full">
                            <div className="relative w-24">
                              <PiFlag className="absolute left-3 top-5 transform -translate-y-1/2 text-muted-foreground" />
                              <FormField
                                key="countryCode"
                                control={form.control}
                                name="countryCode"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl
                                      onChange={() => form.clearErrors()}
                                    >
                                      <Input
                                        className="pl-9 rounded-r-none"
                                        type="text"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <div className="relative w-full">
                              <PiPhone className="absolute left-3 top-5 transform -translate-y-1/2 text-muted-foreground" />
                              <FormField
                                key="phone"
                                control={form.control}
                                name="phone"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl
                                      onChange={() => form.clearErrors()}
                                    >
                                      <Input
                                        className="pl-9 rounded-l-none"
                                        type="text"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
                <div className="flex items-center justify-end gap-2 pr-2">
                  <Button
                    id="clone-submit-button"
                    onClick={form.handleSubmit(onClone)}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? messages.reportsCloneReportButtonTextSubmitting
                      : messages.reportsCloneReportButtonText}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
